'use client';

import React, { useEffect, useRef } from 'react';
import PublicPlayerHero from './PublicPlayerHero';
import PublicProgramGuide from './PublicProgramGuide';
import RumbaHeroBanner from './RumbaHeroBanner';
import RumbaAboutSection from './RumbaAboutSection';
import RumbaSocialSection from './RumbaSocialSection';
import RumbaSlideshow from './RumbaSlideshow';
import RumbaEmailSubscribe from './RumbaEmailSubscribe';
import { PlaybackProvider } from '@/lib/playbackEngine';
import { listenerService } from '@/lib/services/radioService';
import Link from 'next/link';

export default function PublicListenerContent() {
  const sessionIdRef = useRef<string | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let mounted = true;

    const startSession = async () => {
      const id = await listenerService.joinSession();
      if (!mounted || !id) return;
      sessionIdRef.current = id;

      heartbeatRef.current = setInterval(() => {
        if (sessionIdRef.current) {
          listenerService.heartbeat(sessionIdRef.current);
        }
      }, 30_000);
    };

    startSession();

    const handleUnload = () => {
      if (sessionIdRef.current) {
        listenerService.leaveSession(sessionIdRef.current);
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      mounted = false;
      window.removeEventListener('beforeunload', handleUnload);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (sessionIdRef.current) {
        listenerService.leaveSession(sessionIdRef.current);
      }
    };
  }, []);

  return (
    <PlaybackProvider>
      <div className="min-h-screen" style={{ background: 'var(--background)' }}>

        {/* ── Top Navigation Bar ── */}
        <nav
          className="sticky top-0 z-40 flex items-center justify-between px-6 md:px-12 py-3"
          style={{
            background: 'rgba(0, 0, 0, 0.98)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(239,68,68,0.15)',
          }}
        >
          {/* Logo / Brand */}
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #ef4444, #fb923c)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18V5l12-2v13" />
                <circle cx="6" cy="18" r="3" />
                <circle cx="18" cy="16" r="3" />
              </svg>
            </div>
            <span
              className="font-black text-sm tracking-tight"
              style={{
                background: 'linear-gradient(135deg, #ffffff, #fb923c)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              RUMBAEXPLOSIONS
            </span>
          </div>

          {/* Nav links */}
          <div className="flex items-center gap-1">
            <a
              href="#player"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
            >
              Player
            </a>
            <a
              href="#schedule"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
            >
              Programación
            </a>
            <a
              href="#about"
              className="text-xs font-semibold px-3 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-all duration-150"
            >
              Nosotros
            </a>
            <Link
              href="/sign-up-login-screen"
              className="ml-2 text-xs font-bold px-3 py-1.5 rounded-lg transition-all duration-150"
              style={{
                background: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5',
              }}
            >
              Admin
            </Link>
          </div>
        </nav>

        {/* ── Hero Banner ── */}
        <RumbaHeroBanner />

        {/* ── Salsa Slideshow ── */}
        <div className="mx-4 md:mx-auto md:max-w-6xl overflow-hidden rounded-2xl mt-2">
          <RumbaSlideshow />
        </div>

        {/* ── Audio Player (centerpiece) ── */}
        <section id="player" className="relative">
          {/* Section label */}
          <div className="flex items-center justify-center pt-4 pb-2">
            <div
              className="flex items-center gap-2 px-4 py-1.5 rounded-full"
              style={{
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.2)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-pulse" />
              <span className="text-xs font-bold text-red-400 uppercase tracking-widest">Reproduciendo Ahora</span>
            </div>
          </div>

          {/* Player card wrapper */}
          <div
            className="mx-4 md:mx-auto md:max-w-6xl rounded-3xl overflow-hidden mb-2"
            style={{
              background: 'rgba(22,22,35,0.7)',
              border: '1px solid rgba(239,68,68,0.15)',
              boxShadow: '0 0 60px rgba(239,68,68,0.08), 0 20px 60px rgba(0,0,0,0.5)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <PublicPlayerHero />
          </div>
        </section>

        {/* ── Program Guide ── */}
        <section id="schedule" className="mt-4">
          <PublicProgramGuide />
        </section>

        {/* ── About Section ── */}
        <section id="about">
          <div
            className="mx-6 md:mx-12 my-4"
            style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.2), transparent)' }}
          />
          <RumbaAboutSection />
        </section>

        {/* ── Social Section ── */}
        <section id="social">
          <div
            className="mx-6 md:mx-12 my-4"
            style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(124,58,237,0.2), transparent)' }}
          />
          <RumbaSocialSection />
        </section>

        {/* ── Footer ── */}
        <footer
          className="mt-8 px-6 md:px-12 py-10"
          style={{
            borderTop: '1px solid rgba(239,68,68,0.12)',
            background: 'rgba(13,13,20,0.8)',
          }}
        >
          {/* Email Subscribe */}
          <div
            className="max-w-5xl mx-auto mb-8 pb-8"
            style={{ borderBottom: '1px solid rgba(239,68,68,0.1)' }}
          >
            <RumbaEmailSubscribe />
          </div>

          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Brand */}
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #db2424ff, #fb923c)' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              </div>
              <span className="text-sm font-black text-foreground tracking-tight">RUMBAEXPLOSIONS</span>
            </div>

            {/* Center info */}
            <p className="text-xs text-muted-foreground text-center">
              Broadcasting live 24/7 · Medellín, Colombia · Powered by automation
            </p>

            {/* Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://rumbaexplosion.co"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                rumbaexplosion.co
              </a>
              <Link href="/sign-up-login-screen" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                Admin
              </Link>
            </div>
          </div>
        </footer>

      </div>
    </PlaybackProvider>
  );
}